import React, { useState, useEffect, useRef } from 'react';
import SoundCatalog from '../../components/SoundCatalog/SoundCatalog';
import fullCatalogFiles from '../../data/fullCatalogFiles.json';
import { getFullCatalogTrackUrl } from '../../utils/catalogAudio';
import { resetMobileZoom } from '../../utils/resetMobileZoom';
import { getCatalogContactMailto } from '../../utils/catalogContact';
import { useLocale } from '../../utils/catalogI18n';
import '../../components/SoundCatalog/SoundCatalog.css';
import './FullSoundCatalog.css';

const AUTH_KEY = 'otr_full_catalog_auth';
const PASSWORD = process.env.REACT_APP_FULL_CATALOG_PASSWORD || 'akira';

function stripTrackFromUrl() {
  const params = new URLSearchParams(window.location.search);
  if (!params.has('track')) return;
  params.delete('track');
  const query = params.toString();
  const nextUrl = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash}`;
  window.history.replaceState(null, '', nextUrl);
}

function CatalogGate({ onUnlock }) {
  const t = useLocale();
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  const passwordRef = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia('(max-width: 640px)');
    if (!mq.matches) passwordRef.current?.focus();
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (value === PASSWORD) {
      sessionStorage.setItem(AUTH_KEY, '1');
      stripTrackFromUrl();
      passwordRef.current?.blur();
      resetMobileZoom();
      onUnlock();
      setError('');
      return;
    }
    setError(t.passwordError);
  };

  return (
    <div className="full-catalog-gate">
      <form className="full-catalog-gate-card" onSubmit={submit}>
        <h1 className="full-catalog-gate-title">{t.catalogTitle}</h1>
        <input
          ref={passwordRef}
          type="password"
          placeholder={t.passwordPlaceholder}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoComplete="current-password"
          enterKeyHint="go"
        />
        <button type="submit">{t.enter}</button>
        {error && <p className="full-catalog-gate-error">{error}</p>}
      </form>
    </div>
  );
}

const FullSoundCatalog = () => {
  const t = useLocale();
  const [unlocked, setUnlocked] = useState(false);

  useEffect(() => {
    const isUnlocked = sessionStorage.getItem(AUTH_KEY) === '1';
    setUnlocked(isUnlocked);
    if (isUnlocked) stripTrackFromUrl();
  }, []);

  useEffect(() => {
    if (unlocked) resetMobileZoom();
  }, [unlocked]);

  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const root = document.getElementById('root');
    html.classList.add('full-catalog-route');
    const prevHtmlOverflow = html.style.overflow;
    const prevBodyOverflow = body.style.overflow;
    const prevBodyHeight = body.style.height;
    const prevRootHeight = root?.style.height ?? '';
    html.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.height = '100%';
    if (root) root.style.height = '100%';
    return () => {
      html.classList.remove('full-catalog-route');
      html.style.overflow = prevHtmlOverflow;
      body.style.overflow = prevBodyOverflow;
      body.style.height = prevBodyHeight;
      if (root) root.style.height = prevRootHeight;
    };
  }, []);

  if (!unlocked) {
    return <CatalogGate onUnlock={() => setUnlocked(true)} />;
  }

  return (
    <div className="full-sound-catalog-page">
      <SoundCatalog
        tracks={fullCatalogFiles}
        getTrackUrl={getFullCatalogTrackUrl}
        title={t.catalogTitle}
        infoTooltip={
          <>
            <p className="sound-catalog-info-popover-lead">{t.infoLead}</p>
            <p>{t.infoBody}</p>
            <p className="sound-catalog-info-popover-note">{t.infoNote}</p>
            <p className="sound-catalog-info-popover-contact">
              {t.infoContactBefore}
              <a
                href={getCatalogContactMailto()}
                onClick={(e) => e.stopPropagation()}
              >
                {t.infoContactLink}
              </a>
              {t.infoContactAfter}
            </p>
          </>
        }
        mode="page"
        metadataMode="full"
        accessLabel={t.privateAccess}
      />
    </div>
  );
};

export default FullSoundCatalog;
